/**
 * Módulo de Autenticación de Dispositivos
 * Maneja el fingerprinting y autenticación de dispositivos
 */

import { generateDeviceFingerprint, authenticateDevice, renewSession, getSessionToken, getDisplayType, getPanelLayout, getDeviceCode, setDeviceCode } from './modules/device-auth.js';
import { panelTargetUrl } from './modules/panel-target.js';
import { CONFIG as APP_CONFIG } from './config.js';

// Configuración - usar window.APP_CONFIG si está disponible (inyectado por config.runtime.js)
const CONFIG = {
  apiUrl: window.APP_CONFIG?.API_URL || (
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:33000'
      : `${window.location.protocol}//${window.location.host}`
  ),
  enableDeviceAuth: true,
  sessionRenewalInterval: 15 * 60 * 1000 // 15 minutos
};

let deviceFingerprint = null;
let isAuthenticated = false;

/**
 * Inicializa la autenticación del dispositivo
 * @returns {Promise<boolean>} true si la autenticación fue exitosa
 */
export async function initAuth() {
  if (!CONFIG.enableDeviceAuth) {
    isAuthenticated = true;
    return true;
  }

  try {
    // Generar fingerprint
    deviceFingerprint = await generateDeviceFingerprint();
    
    // Verificar sesión válida
    const sessionToken = getSessionToken();
    if (sessionToken) {
      const sessionExpires = localStorage.getItem('kiosko_session_expires');
      if (sessionExpires && new Date(sessionExpires) > new Date()) {
        // Renovar al arrancar trae el tipo de pantalla actual: si la oficina lo ha cambiado
        // (kiosko, tele, tablet de taller) la app arranca ya con la vista que toca.
        const renewed = await renewSession(CONFIG.apiUrl).then(() => true, () => false);
        if (renewed) {
          isAuthenticated = true;
          setupSessionRenewal();
          return true;
        }
      }
    }
    
    // Autenticar dispositivo
    await authenticateDevice(CONFIG.apiUrl);
    isAuthenticated = true;
    setupSessionRenewal();
    return true;
    
  } catch (error) {
    console.error('Auth error:', error);
    
    if (error.message === 'DEVICE_NOT_REGISTERED') {
      // Intentar registrar el dispositivo
      const registered = await registerAccessAttempt();
      if (registered) {
        // El dispositivo fue registrado y autenticado exitosamente
        return true;
      }
      // Si no se autenticó, mostrar pantalla de no registrado
      showAuthScreen('not-registered');
    } else if (error.message === 'DEVICE_PENDING_APPROVAL') {
      showAuthScreen('pending');
    } else if (error.message === 'DEVICE_BLOCKED') {
      showAuthScreen('blocked');
    } else if (error.message === 'NETWORK_ERROR') {
      showAuthScreen('network-error');
    } else {
      showAuthScreen('not-registered');
    }
    
    isAuthenticated = false;
    return false;
  }
}

/**
 * Registra un intento de acceso en el servidor
 * @returns {Promise<boolean>} true si el dispositivo fue autenticado exitosamente
 */
async function registerAccessAttempt() {
  try {
    const deviceInfo = {
      fingerprint: deviceFingerprint,
      userAgent: navigator.userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    const response = await fetch(`${CONFIG.apiUrl}/api/hr/devices/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(deviceInfo)
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('Device registration result:', result);
      setDeviceCode(result.device?.deviceId);
      
      // Si el dispositivo está activo, intentar autenticarse
      if (result.device?.status === 'ACTIVE') {
        console.log('Device is active, attempting authentication...');
        try {
          await authenticateDevice(CONFIG.apiUrl);
          isAuthenticated = true;
          setupSessionRenewal();
          return true;
        } catch (authError) {
          console.error('Authentication after registration failed:', authError);
          return false;
        }
      } else if (result.device?.status === 'PENDING') {
        showAuthScreen('pending');
        return false;
      }
    }
    return false;
  } catch (error) {
    console.error('Failed to register access attempt:', error);
    return false;
  }
}

/**
 * Configura la renovación automática de sesión
 */
function setupSessionRenewal() {
  const initialType = getDisplayType();
  setInterval(async () => {
    try {
      await renewSession(CONFIG.apiUrl);
      // Si desde el backoffice han cambiado el tipo a 'panel', la tele se va al tablero.
      if (await redirectToPanelIfNeeded()) return;
      // Cambio entre kiosko y tablet de taller: recargar para arrancar con la vista que toca.
      if (getDisplayType() !== initialType) window.location.reload();
    } catch (error) {
      console.error('Session renewal failed:', error);
    }
  }, CONFIG.sessionRenewalInterval);
}

/**
 * Si el dispositivo es 'panel', la app de kiosko solo hace de portero:
 * manda la sesión al backoffice (#/panel/interactivo o #/panel según
 * display_config.layout) y no carga ningún módulo.
 * @returns {boolean} true si se ha redirigido
 */
export async function redirectToPanelIfNeeded() {
  if (getDisplayType() !== 'panel') return false;
  // Token fresco siempre: el panel renueva la sesión por su cuenta y el que guarda
  // esta app puede estar ya rotado (p. ej. tras reiniciar la tele).
  try {
    await authenticateDevice(CONFIG.apiUrl);
  } catch (error) {
    console.warn('No se pudo reautenticar antes de ir al panel; se usa el token guardado', error);
  }
  const token = getSessionToken();
  if (!token) return false;
  // display_config.layout: táctil (#/panel/interactivo, por defecto) o clásica (#/panel).
  const target = panelTargetUrl(APP_CONFIG.backofficeUrl, token, getPanelLayout());
  window.location.replace(target);
  return true;
}

/**
 * Muestra una pantalla de autenticación
 */
function showAuthScreen(type) {
  const app = document.getElementById('app');
  app.classList.remove('auth-pending');
  
  // Mismo texto que las apps de obra y taller: qué dispositivo es y dónde se activa.
  const code = getDeviceCode();
  const deviceLabel = code ? `el dispositivo <strong>${code}</strong>` : 'este dispositivo';
  const activateHint = `Pide a la oficina que active ${deviceLabel} en Administración → Dispositivos, ` +
    'con el tipo que le toque: «Kiosko», «Panel informativo» o «Kiosko de taller (fases)».';

  const screens = {
    'not-registered': {
      icon: 'bi-shield-exclamation',
      color: 'var(--danger)',
      title: 'Dispositivo sin registrar',
      message: 'No se ha podido registrar este dispositivo en el sistema.',
      instruction: activateHint
    },
    'pending': {
      icon: 'bi-hourglass-split',
      color: 'var(--warning)',
      title: 'Kiosko pendiente de activar',
      message: activateHint,
      instruction: ''
    },
    'blocked': {
      icon: 'bi-ban',
      color: 'var(--danger)',
      title: 'Kiosko bloqueado',
      message: `${code ? `El dispositivo <strong>${code}</strong> está bloqueado` : 'Este dispositivo está bloqueado'}.`,
      instruction: 'Habla con la oficina: puede desbloquearlo en Administración → Dispositivos.'
    },
    'network-error': {
      icon: 'bi-wifi-off',
      color: 'var(--warning)',
      title: 'Sin conexión',
      message: 'No se puede conectar con el servidor de autenticación.',
      instruction: 'Verifica la conexión de red.'
    }
  };

  const screen = screens[type];
  
  app.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      padding: 2rem;
      text-align: center;
    ">
      <i class="bi ${screen.icon}" style="font-size: clamp(4rem, 15vw, 8rem); color: ${screen.color}; margin-bottom: 2rem;"></i>
      <h1 style="font-size: clamp(1.5rem, 6vw, 3rem); font-weight: 700; margin-bottom: 1rem; color: ${screen.color};">
        ${screen.title}
      </h1>
      <p style="font-size: clamp(1rem, 4vw, 1.5rem); margin-bottom: 2rem; max-width: 600px;">
        ${screen.message}
      </p>
      <p style="font-size: clamp(0.875rem, 3vw, 1.125rem); color: var(--text-secondary); max-width: 500px;">
        ${screen.instruction}
      </p>
      <p style="margin-top: 2rem; font-size: 0.875rem; color: var(--text-secondary);">
        Se vuelve a comprobar cada 15 segundos.
      </p>
    </div>
  `;

  // Como en obra y taller: en cuanto la oficina lo aprueba (o vuelve la red), arranca solo.
  setTimeout(() => location.reload(), 15000);
}

/**
 * Cierra la sesión del dispositivo
 */
export function logout() {
  localStorage.removeItem('kiosko_device_fingerprint');
  localStorage.removeItem('kiosko_session_token');
  localStorage.removeItem('kiosko_session_expires');
  location.reload();
}

export { isAuthenticated, CONFIG };
